import { useEffect, useMemo, useRef, type FunctionComponent } from 'react';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { Box, FlexBox } from '@ardoq/layout';
import { Header4 } from '@ardoq/typography';
import { t } from '@ardoq/i18n';
import { ViewIds } from '@ardoq/api-types';
import { WILDCARD } from '@ardoq/common-helpers';
import { ViewLegendContainer } from '@ardoq/graph';
import { ViewLegend } from '@ardoq/view-legend';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { HeightOffset } from 'views/viewLegend/useViewLegendSubscription';
import type {
  AqBlocks,
  BlocksLayoutStyle,
  BlocksLinksAggregation,
} from 'tabview/visualizations/aq-blocks/types';
import { useRepaintOnImageReady } from 'tabview/assistantShared/useRepaintOnImageReady';
import { useBlocksTooltip } from 'tabview/assistantShared/blocksTooltip';
import { createContextMenuHandler } from 'tabview/flexibleBlockDiagram/contextMenu';
import { setContextMenuState } from 'contextMenus/contextMenuState$';
import {
  toGroupsOffSavedCollapse,
  useSaveGroupCollapse,
} from 'tabview/assistantShared/collapsedGroups';
import { handleStoreSetting } from 'viewSettings/settingsHelper';
import { CenteredMessage } from 'tabview/CenteredMessage';
import { useZoomActions } from 'tabview/assistantShared/useZoomActions';
import {
  clearSubscriptions,
  subscribeToAction,
} from 'streams/utils/streamUtils';
// Registers the <aq-blocks> custom element (customElements.define side effect).
import 'tabview/visualizations/aq-blocks/aq-blocks';
import { AssistantBlocksViewModel, viewModel$ } from './viewModel$';
import {
  ASSISTANT_BLOCKS_ZOOM_ACTIONS,
  assistantBlocksCollapseAll,
  assistantBlocksExpandAll,
} from './assistantBlocks$';

const saveCollapsedGroupIds = (collapsedGroupIds: string[]) =>
  handleStoreSetting(ViewIds.ASSISTANT_BLOCKS, { collapsedGroupIds });

const closeContextMenu = () => dispatchAction(setContextMenuState(null));

/* `type: 'manual'` is what this view needs: `buildBlocksGraph` places every child itself for
   `groupLayoutMode: 'manual'`, and the element's default `'process'` would lay them out again.
   The orientation is the element's own default, kept so the whole style is stated in one
   place rather than half-inherited. */
const toLayoutStyle = (
  linksAggregation: BlocksLinksAggregation
): BlocksLayoutStyle => ({
  type: 'manual',
  linksAggregation,
  orientation: 'left-to-right',
});

export const AssistantBlocks = (props: AssistantBlocksViewModel) => {
  const {
    graph,
    i18nLocale,
    title,
    error,
    isLegendActive,
    legendProps,
    linksAggregation,
    layoutKey,
    collapsedGroupIds,
  } = props;
  const elementRef = useRef<AqBlocks | null>(null);

  useRepaintOnImageReady(elementRef);
  const { onEdited: handleEdited, applySavedCollapse } = useSaveGroupCollapse(
    saveCollapsedGroupIds,
    collapsedGroupIds
  );

  useEffect(() => {
    const subscriptions = [
      subscribeToAction(assistantBlocksExpandAll, () =>
        applySavedCollapse([], () => {
          elementRef.current?.expandAll();
          elementRef.current?.zoomFit();
        })
      ),
      subscribeToAction(assistantBlocksCollapseAll, () =>
        applySavedCollapse([WILDCARD], () => {
          elementRef.current?.collapseAll();
          elementRef.current?.zoomFit();
        })
      ),
    ];
    return () => {
      clearSubscriptions(subscriptions);
    };
  }, [applySavedCollapse]);

  useEffect(() => {
    const element = elementRef.current;
    const elementGraph = element?.graph;
    if (!element || !elementGraph) return;

    const { toCollapse, toExpand } = toGroupsOffSavedCollapse(
      elementGraph,
      collapsedGroupIds
    );
    if (toCollapse.length === 0 && toExpand.length === 0) return;

    applySavedCollapse(collapsedGroupIds, () => {
      element.collapse(toCollapse);
      element.expand(toExpand);
    });
  }, [collapsedGroupIds, applySavedCollapse]);

  /* aq-blocks re-fits only when most nodes are new, and an arrangement or grouping change reuses
     nearly all of them while moving every one. */
  useEffect(() => {
    elementRef.current?.zoomFit();
  }, [layoutKey]);

  useZoomActions(elementRef, ASSISTANT_BLOCKS_ZOOM_ACTIONS);

  const { doTooltip, tooltipPopover } = useBlocksTooltip();
  const layoutStyle = useMemo(
    () => toLayoutStyle(linksAggregation),
    [linksAggregation]
  );
  // Stable identity: aq-blocks cancels the open context menu whenever the
  // doContextMenu prop changes, so recreating it each render would dismiss it.
  const doContextMenu = useMemo(
    () =>
      createContextMenuHandler(
        { viewId: ViewIds.ASSISTANT_BLOCKS, i18nLocale },
        closeContextMenu
      ),
    [i18nLocale]
  );

  if (!graph) {
    if (error) {
      return (
        <CenteredMessage
          title={t(
            i18nLocale,
            'assistantBlocks:errorState.title',
            'Could not lay out the blocks'
          )}
          description={error}
        />
      );
    }
    return (
      <CenteredMessage
        title={t(
          i18nLocale,
          'assistantBlocks:emptyState.title',
          'No blocks yet'
        )}
        description={t(
          i18nLocale,
          'assistantBlocks:emptyState.description',
          'This view has no components to lay out.'
        )}
      />
    );
  }

  return (
    <Box
      className="prevent-context-change"
      width="full"
      height="full"
      position="relative"
    >
      <FlexBox flexDirection="column" width="full" height="full">
        {title && (
          <Box padding="small">
            <Header4>{title}</Header4>
          </Box>
        )}
        <FlexBox flex={1} position="relative">
          {/* `layoutStyle` before `graph`: React sets custom-element properties in source
              order, and the element's `layoutStyle` handler is guarded on `this.graph`, so
              setting it first is a plain store. The other order lays the graph out under the
              element's default `type: 'process'` and then, seeing the type change,
              `resetLayout`s every group to `layoutType: undefined` — dropping the `'manual'`
              that `buildBlocksGraph` set and leaving them permanently dirty. */}
          <aq-blocks
            ref={elementRef}
            layoutStyle={layoutStyle}
            graph={graph}
            /* Read-only until aq-blocks can edit grouped manual layouts: ARD-33703. */
            isReadonly
            doTooltip={doTooltip}
            doContextMenu={doContextMenu}
            onEdited={handleEdited}
            style={{ position: 'absolute', inset: 0 }}
          />
        </FlexBox>
      </FlexBox>
      <HeightOffset>
        {heightOffset => (
          <ViewLegendContainer
            heightOffset={heightOffset}
            visible={isLegendActive}
          >
            <ViewLegend
              {...legendProps}
              labels={getViewLegendLabels(i18nLocale)}
            />
          </ViewLegendContainer>
        )}
      </HeightOffset>
      {tooltipPopover}
    </Box>
  );
};

export const ViewpointModeAssistantBlocks: FunctionComponent = connect(
  AssistantBlocks,
  viewModel$
);
